<template>
    <Link
        :href="href"
        class="block bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700 hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-600 transition-all"
    >
        <div class="p-6">
            <div class="flex items-center justify-between mb-4">
                <div class="flex items-center space-x-3">
                    <div :class="[
                        'p-2 rounded-lg',
                        iconBgClass
                    ]">
                        <component :is="iconComponent" :class="['w-6 h-6', iconColorClass]" />
                    </div>
                    <h3 class="text-lg font-semibold text-secondary-900 dark:text-white">{{ title }}</h3>
                </div>
            </div>

            <div class="flex items-center justify-between">
                <!-- Large Total -->
                <div>
                    <p class="text-5xl font-bold" :class="textColorClass">{{ total }}</p>
                    <p class="text-sm text-secondary-500 dark:text-secondary-400 mt-1">Total Open</p>
                </div>

                <!-- Breakdown on the right -->
                <div class="space-y-1 text-right">
                    <div v-for="(value, key) in breakdown" :key="key" class="flex items-center justify-between space-x-4">
                        <span class="text-xs text-secondary-500 dark:text-secondary-400 capitalize">
                            {{ formatLabel(key) }}
                        </span>
                        <span class="text-sm font-semibold text-secondary-700 dark:text-secondary-300">
                            {{ value }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </Link>
</template>

<script setup>
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';
import { TicketIcon, WrenchIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
    title: {
        type: String,
        required: true,
    },
    total: {
        type: Number,
        required: true,
    },
    breakdown: {
        type: Object,
        required: true,
    },
    icon: {
        type: String,
        required: true,
    },
    color: {
        type: String,
        default: 'blue',
    },
    href: {
        type: String,
        required: true,
    },
});

const iconComponent = computed(() => {
    const icons = {
        'TicketIcon': TicketIcon,
        'WrenchIcon': WrenchIcon,
        'ExclamationTriangleIcon': ExclamationTriangleIcon,
    };
    return icons[props.icon] || TicketIcon;
});

const iconBgClass = computed(() => {
    const classes = {
        'blue': 'bg-blue-100 dark:bg-blue-900/30',
        'green': 'bg-green-100 dark:bg-green-900/30',
        'red': 'bg-red-100 dark:bg-red-900/30',
        'purple': 'bg-purple-100 dark:bg-purple-900/30',
        'yellow': 'bg-yellow-100 dark:bg-yellow-900/30',
    };
    return classes[props.color] || classes.blue;
});

const iconColorClass = computed(() => {
    const classes = {
        'blue': 'text-blue-600 dark:text-blue-400',
        'green': 'text-green-600 dark:text-green-400',
        'red': 'text-red-600 dark:text-red-400',
        'purple': 'text-purple-600 dark:text-purple-400',
        'yellow': 'text-yellow-600 dark:text-yellow-400',
    };
    return classes[props.color] || classes.blue;
});

const textColorClass = computed(() => {
    const classes = {
        'blue': 'text-blue-600 dark:text-blue-400',
        'green': 'text-green-600 dark:text-green-400',
        'red': 'text-red-600 dark:text-red-400',
        'purple': 'text-purple-600 dark:text-purple-400',
        'yellow': 'text-yellow-600 dark:text-yellow-400',
    };
    return classes[props.color] || classes.blue;
});

function formatLabel(key) {
    return key.replace(/_/g, ' ');
}
</script>
